/**
 * 到访的匿名计数(PRD 模块 BJ,ADR-118):网页每次整页加载时通知自家服务器「有人打开了一页」,服务器把访客所在的洲、国家、省州、城市(取自 Cloudflare 边缘的
 * `request.cf`,只有 Worker 里拿得到;Cloudflare 的 GraphQL 分析接口没有省州与城市)与界面语言写进 Workers Analytics Engine 的 `aerofox_visits` 数据集。
 * 客户端(apps/web、apps/sim)与服务器(apps/api)共用这份约定。**请求体只有界面语言**:没有 cookie、账号、设备标识、网址或 IP;服务器不存 IP 与 User-Agent
 * (User-Agent 只用来当场丢掉自称爬虫的请求),所以不能把两条数据连到同一个人。硬规则 5:自家指标只走 Analytics Engine 经自家 Worker。纯常量与纯函数,无依赖。
 */

/** 界面语言标签的形状(en、zh-CN、pt-BR……),与课程计数同一条。 */
export const VISIT_METRIC_LOCALE = /^[a-z]{2}(-[A-Z]{2})?$/;

/** 地理字段(洲、国家、省州、城市)记下来的最大长度。 */
export const VISIT_GEO_MAX_CHARS = 64;

export interface VisitMetricBody {
  locale?: string;
}

/** 核对一条上报;不合约定(不是对象、有多余字段)返回 null。多余字段一律拒收:这条通道只许带界面语言。 */
export function parseVisitMetric(body: unknown): { locale: string } | null {
  if (!body || typeof body !== "object" || Array.isArray(body)) return null;
  const b = body as Record<string, unknown>;
  if (Object.keys(b).some((k) => k !== "locale")) return null;
  if (b.locale === undefined) return { locale: "" };
  return typeof b.locale === "string" && VISIT_METRIC_LOCALE.test(b.locale) ? { locale: b.locale } : null;
}

/** `request.cf` 的地理字段可能是 null / undefined / 数字(本地开发、Tor、判定不出):统一成去掉控制字符、限长的字符串,没有就是空串。 */
export function cleanGeoField(value: unknown): string {
  if (typeof value !== "string") return "";
  // eslint-disable-next-line no-control-regex
  const s = value.replace(/[\u0000-\u001f\u007f-\u009f‎‏‪-‮⁦-⁩]/g, "").trim();
  return [...s].slice(0, VISIT_GEO_MAX_CHARS).join("");
}

/** 浏览器里表示「请不要跟踪我」的两个信号:Do Not Track 与 Global Privacy Control。任何一个开着,就不报到访(即使这条计数本来就不能识别个人)。 */
export interface PrivacySignals {
  doNotTrack?: string | null | undefined;
  globalPrivacyControl?: boolean | undefined;
}
export function visitAllowed(nav: PrivacySignals | undefined): boolean {
  if (!nav) return true;
  return nav.doNotTrack !== "1" && nav.globalPrivacyControl !== true;
}

/**
 * 到访上报的请求(给 `fetch` 用):`POST <apiUrl>/api/metrics/visit`,JSON 体只有界面语言(格式不对就省掉),不带 cookie(`credentials: "omit"`),
 * `keepalive` 让页面马上被关掉时也发得出去。`apiUrl` 为空(本地开发没配)返回 null = 不报。
 */
export interface VisitRequest {
  url: string;
  init: { method: "POST"; credentials: "omit"; keepalive: true; headers: Record<string, string>; body: string };
}
export function visitRequest(apiUrl: string, locale: string | undefined): VisitRequest | null {
  if (!apiUrl) return null;
  const body: VisitMetricBody = VISIT_METRIC_LOCALE.test(locale ?? "") ? { locale: locale! } : {};
  return { url: `${apiUrl.replace(/\/+$/, "")}/api/metrics/visit`, init: { method: "POST", credentials: "omit", keepalive: true, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) } };
}
